'use client';
import { atom, useAtom } from 'jotai';
import { useSteps } from './useSteps';
import {
    Steps,
    Step,
    EStepperPaths,
} from '@/types/acquistion/atomic-sell-form.types';

const currentIndexAtom = atom(0);

export const useStepper = () => {
    const [currentIndex, setCurrentIndexAtom] = useAtom(currentIndexAtom);
    const {
        inspectionTypeStep,
        sylndrHubsStep,
        makeStep,
        modelStep,
        yearStep,
        kilometerStep,
        transmitionStep,
        locationStep,
        contactInfoStep,
    } = useSteps();

    //Treated as Array (order matters)
    const StepsOrder: { [key in Steps]?: Step<any> } = {
        [inspectionTypeStep.name]: inspectionTypeStep,
        [sylndrHubsStep.name]: sylndrHubsStep,
        [makeStep.name]: makeStep,
        [modelStep.name]: modelStep,
        [yearStep.name]: yearStep,
        [kilometerStep.name]: kilometerStep,
        [transmitionStep.name]: transmitionStep,
        [locationStep.name]: locationStep,
        [contactInfoStep.name]: contactInfoStep,
    };

    const Next = (arg?: number, toNextUnpopulatedStep?: boolean): void => {
        arg = arg ?? currentIndex;

        if (arg === Object.values(StepsOrder).length - 1) {
            return;
        }

        const steps = Object.values(StepsOrder);
        const currentStep = steps[arg];

        const isCurrentStepSkiped = currentStep.checkSkipArray.reduce(
            (acc, func) => acc || func(),
            false
        );

        if (
            (!currentStep.value ||
                currentStep.value === currentStep.defaultValue) &&
            !isCurrentStepSkiped
        ) {
            return;
        }
        const firstUnpopulatedStep = steps.findIndex(step => !step.value);

        const index = toNextUnpopulatedStep ? firstUnpopulatedStep : arg + 1;

        const nextStep = steps[index];

        clearSteps(currentStep, index);
        if (
            nextStep.checkSkipArray.reduce((acc, func) => acc || func(), false)
        ) {
            Next(index);
        } else {
            setCurrentIndexAtom(index);
        }
    };

    const clearSteps = (currentStep: Step<any>, index: number) => {
        if (currentStep.noneDependantSteps.length > 0) {
            clearOnlyDependantSteps(index);
        } else {
            clearAllFromIndex(index);
        }
    };

    const clearAllFromIndex = (index: number) => {
        const steps = Object.values(StepsOrder);

        for (let i = index; i < steps.length; i++) {
            steps[i].setValue(steps[i].defaultValue);
        }
    };

    const clearOnlyDependantSteps = (index: number) => {
        const steps = Object.values(StepsOrder);
        const noneDependantSteps = steps[index - 1].noneDependantSteps;
        for (let i = index; i < steps.length; i++) {
            if (!noneDependantSteps.includes(steps[i].name)) {
                steps[i].setValue(steps[i].defaultValue);
            }
        }
    };

    const Back = (arg?: number): void => {
        arg = arg ?? currentIndex;

        if (currentIndex === 0) {
            return;
        }

        const index = arg - 1;

        const step = Object.values(StepsOrder)[index];

        if (reduceCheckSkip(step.checkSkipArray) || !step.replaceAllOnNext) {
            Back(index);
        } else {
            setCurrentIndexAtom(index);
        }
    };

    const reduceCheckSkip = (checkSkipArray: (() => boolean)[]) => {
        if (checkSkipArray.length === 0) {
            return false;
        }

        return checkSkipArray.reduce((acc, func) => acc || func(), false);
    };

    const getPath = (index?: number): EStepperPaths => {
        const steps = index
            ? Object.values(StepsOrder).slice(0, index)
            : Object.values(StepsOrder);

        const isLead = steps
            .filter(step => !reduceCheckSkip(step.checkSkipArray))
            .reduce((acc, step) => acc && step.value?.inShoppingList, true);

        return isLead ? EStepperPaths.PATH_1 : EStepperPaths.PATH_2;
    };

    const checkShoppingList = (index?: number) => {
        const steps = index
            ? Object.values(StepsOrder).slice(0, index)
            : Object.values(StepsOrder);

        const usedSteps = steps.filter(
            step => !reduceCheckSkip(step.checkSkipArray)
        );

        for (const step of usedSteps) {
            if (step.value) {
                if (!step.value.inShoppingList) return false;
            }
        }
        return true;
    };

    return {
        StepsOrder,
        currentIndex,
        checkShoppingList,
        Next,
        Back,
        reduceCheckSkip,
        setCurrentIndexAtom,
        clearAllFromIndex,
        getPath,
    };
};
